import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, StatusBadge, ModalityBadge } from "@/components/Shared";
import { toast } from "sonner";
import { Check, X, Clock, ArrowLeftRight } from "lucide-react";

export default function Requests() {
  const [timeoff, setTimeoff] = useState([]);
  const [swaps, setSwaps] = useState([]);
  const [tab, setTab] = useState("timeoff");

  const load = async () => {
    try {
      const [t, s] = await Promise.all([api.get("/timeoff"), api.get("/swaps")]);
      setTimeoff(t.data);
      setSwaps(s.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const decideTimeoff = async (id, status) => {
    try { await api.patch(`/timeoff/${id}`, { status }); toast.success(`Time-off ${status}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const decideSwap = async (id, status) => {
    try { await api.patch(`/swaps/${id}`, { status }); toast.success(`Swap ${status}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const tabCls = (t) => `px-4 py-2 text-sm font-mono-rs uppercase tracking-wider border-b-2 transition-all duration-300 ${
    tab === t ? "border-[#D902EE] text-white" : "border-transparent text-slate-400 hover:text-white"}`;

  const pendingTO = timeoff.filter((x) => x.status === "pending").length;
  const pendingSW = swaps.filter((x) => x.status === "pending").length;

  return (
    <div>
      <PageHeader title="Requests" subtitle="// Approve or deny staff requests" />

      <div className="flex border-b border-slate-800 mb-6">
        <button data-testid="tab-timeoff" onClick={() => setTab("timeoff")} className={tabCls("timeoff")}>
          Time-off {pendingTO > 0 && <span className="text-[#FFB800]">({pendingTO})</span>}
        </button>
        <button data-testid="tab-swaps" onClick={() => setTab("swaps")} className={tabCls("swaps")}>
          Swaps {pendingSW > 0 && <span className="text-[#FFB800]">({pendingSW})</span>}
        </button>
      </div>

      {tab === "timeoff" && (
        <div className="space-y-3" data-testid="timeoff-list">
          {timeoff.length === 0 && <p className="text-slate-500 text-sm">No time-off requests.</p>}
          {timeoff.map((r) => (
            <div key={r.id} data-testid={`timeoff-${r.id}`} className="bg-[#0F1219] border border-slate-800 p-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-4">
                <Clock className="w-5 h-5 text-[#00F0FF]" />
                <div>
                  <div className="text-white font-medium">{r.tech_name} <span className="text-xs text-slate-500">({r.tech_category})</span></div>
                  <div className="font-mono-rs text-sm text-slate-300">{r.start_date} → {r.end_date}</div>
                  {r.reason && <div className="text-xs text-slate-500 mt-1">{r.reason}</div>}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <StatusBadge status={r.status} />
                {r.status === "pending" && (
                  <div className="flex gap-2">
                    <button data-testid={`approve-timeoff-${r.id}`} onClick={() => decideTimeoff(r.id, "approved")}
                      className="p-2 border border-[#00FF66]/50 text-[#00FF66] hover:bg-[#00FF66]/10 transition-all"><Check className="w-4 h-4" /></button>
                    <button data-testid={`deny-timeoff-${r.id}`} onClick={() => decideTimeoff(r.id, "denied")}
                      className="p-2 border border-[#FF003C]/50 text-[#FF003C] hover:bg-[#FF003C]/10 transition-all"><X className="w-4 h-4" /></button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "swaps" && (
        <div className="space-y-3" data-testid="swaps-list">
          {swaps.length === 0 && <p className="text-slate-500 text-sm">No swap requests.</p>}
          {swaps.map((r) => (
            <div key={r.id} data-testid={`swap-${r.id}`} className="bg-[#0F1219] border border-slate-800 p-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-4">
                <ArrowLeftRight className="w-5 h-5 text-[#D902EE]" />
                <div>
                  <div className="text-white font-medium">{r.requester_name} → {r.target_name}</div>
                  {r.shift && (
                    <div className="flex items-center gap-2 mt-1">
                      <ModalityBadge modality={r.shift.modality} />
                      <span className="font-mono-rs text-sm text-slate-300">{r.shift.date} {r.shift.start_time}–{r.shift.end_time}</span>
                    </div>
                  )}
                  {r.note && <div className="text-xs text-slate-500 mt-1">{r.note}</div>}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <StatusBadge status={r.status} />
                {r.status === "pending" && (
                  <div className="flex gap-2">
                    <button data-testid={`approve-swap-${r.id}`} onClick={() => decideSwap(r.id, "approved")}
                      className="p-2 border border-[#00FF66]/50 text-[#00FF66] hover:bg-[#00FF66]/10 transition-all"><Check className="w-4 h-4" /></button>
                    <button data-testid={`deny-swap-${r.id}`} onClick={() => decideSwap(r.id, "denied")}
                      className="p-2 border border-[#FF003C]/50 text-[#FF003C] hover:bg-[#FF003C]/10 transition-all"><X className="w-4 h-4" /></button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
